import { api } from './auth.service';

// Equivale às funções TCQuery/TCSetField do ADVPL — toda comunicação com
// o backend para o módulo de Tarefas passa por aqui.

const tarefaService = {

  // Listar tarefas. Aceita filtros via objeto (todos opcionais):
  //   - Fase 1/2: { responsavel_id, solicitante_id, status_execucao, incluir_inativos }
  //   - Fase 3 (multi-valor via CSV):
  //       status_execucao: 'N,E,X'
  //       setor:           'C,T'
  //       prioridade:      'A,M'
  //       cliente_ids:     '000001,000002'
  //       negocio_ids:     'ERP-1000,ERP-1001'
  //       solicitante_ids: '000003'
  //       responsavel_ids: '000004,000005'
  //   - Fase 3 (data ranges):
  //       data_prog_de, data_prog_ate     (datetime ISO)
  //       deadline_de,  deadline_ate      (date YYYY-MM-DD)
  //   - Fase 3 (busca textual):
  //       busca: 'palavra'  (procura em título, descrição, ID, cliente, projeto, pessoas)
  //   - Fase 3 (ordenação):
  //       ordem: 'recente'|'antiga'|'deadline'|'prioridade'|'status'|
  //              'data_prog'|'cliente'|'projeto'|'setor'|'solicitante'|'responsavel'
  listar: async (filtros = {}) => {
    const res = await api.get('/tarefas', { params: filtros });
    return res.data;
  },

  // r35 item 5: passa usuario_id pro backend aplicar escopo
  // r241: via_lancamento = id do lançamento de horas de onde a tarefa está
  //       sendo aberta. O backend confere o vínculo e, se bater, libera a
  //       leitura pra quem não é solicitante/responsável/citado.
  buscarPorId: async (id, usuario_id, { via_lancamento } = {}) => {
    const params = {};
    if (usuario_id)     params.usuario_id     = usuario_id;
    if (via_lancamento) params.via_lancamento = via_lancamento;
    const res = await api.get(`/tarefas/${id}`, { params });
    return res.data;
  },

  incluir: async (dados) => {
    const res = await api.post('/tarefas', dados);
    return res.data;
  },

  alterar: async (id, dados) => {
    const res = await api.put(`/tarefas/${id}`, dados);
    return res.data;
  },

  // PATCH parcial — pra edição inline no browse (prioridade, deadline, programação)
  atualizarParcial: async (id, campos, usuario_id) => {
    const res = await api.patch(`/tarefas/${id}`, { ...campos, usuario_id });
    return res.data;
  },

  excluir: async (id, usuario_id) => {
    const res = await api.delete(`/tarefas/${id}`, { data: { usuario_id } });
    return res.data;
  },

  // Botões "Iniciar", "Concluir", "Reabrir"
  // Pra concluir mãe com filhas abertas, passar `force: true` no segundo
  // chamado depois que o usuário confirmar.
  alterarStatusExecucao: async (id, status_execucao, usuario_id, force = false) => {
    const res = await api.patch(`/tarefas/${id}/status-execucao`, {
      status_execucao, usuario_id, force,
    });
    return res.data;
  },

  // Marca como visualizada quando o responsável abre a tarefa
  marcarVisualizado: async (id, usuario_id) => {
    const res = await api.patch(`/tarefas/${id}/visualizar`, { usuario_id });
    return res.data;
  },

  // Timeline da tarefa
  listarAnotacoes: async (id) => {
    const res = await api.get(`/tarefas/${id}/anotacoes`);
    return res.data;
  },

  // ─── Fase 2 ──────────────────────────────────────────────────────────────

  // Sub-tarefas (filhas) de uma tarefa
  listarFilhas: async (id) => {
    const res = await api.get(`/tarefas/${id}/filhas`);
    return res.data;
  },

  // r215: colaboradores citados (@) nos comentários da tarefa — uma linha por
  // pessoa, com quantas vezes foi citada e quando foi a última.
  listarCitados: async (id) => {
    const res = await api.get(`/tarefas/${id}/citados`);
    return res.data;
  },

  // Autocomplete: negócios pra vincular como projeto
  // Se clienteId for passado, restringe aos negócios desse cliente
  buscarNegocios: async (termo, clienteId) => {
    const params = { q: termo };
    if (clienteId) params.cliente_id = clienteId;
    // r167: era '/tarefas/buscar-negocios'. Agora usa a referência compartilhada.
    // Mesmo shape ({ id, descricao, cliente_id, cliente_nome, cliente_tag, cliente_cor_tag }).
    const res = await api.get('/negocios/referencia', { params });
    return res.data;
  },

  // Autocomplete: clientes (quando não há vínculo com negócio)
  buscarClientes: async (termo) => {
    // r167: era '/tarefas/buscar-clientes'. Referência compartilhada.
    // Mesmo shape ({ id, nome_fantasia, razao_social, cnpj, tag, cor_tag }).
    const res = await api.get('/clientes/referencia', { params: { q: termo } });
    return res.data;
  },

  // Autocomplete: tarefas (escolher mãe). Passar excluirId = id da tarefa
  // atual pra não listar ela mesma nem suas descendentes.
  buscarTarefas: async (termo, excluirId) => {
    const params = { q: termo };
    if (excluirId) params.excluir_id = excluirId;
    const res = await api.get('/tarefas/buscar-tarefas', { params });
    return res.data;
  },

  // Lista usuários elegíveis a serem responsáveis (vinculados a colaborador)
  buscarResponsaveis: async () => {
    const res = await api.get('/tarefas/buscar-responsaveis');
    return res.data;
  },

  // Calcula data_fim baseado em data_inicio + duracao_horas + responsavel_id,
  // considerando horário comercial do colaborador do responsável e feriados.
  // Retorna { data_fim, observacao? }
  calcularFim: async (data_inicio, duracao_horas, responsavel_id) => {
    const params = { data_inicio, duracao_horas };
    if (responsavel_id) params.responsavel_id = responsavel_id;
    const res = await api.get('/tarefas/calcular-fim', { params });
    return res.data;
  },

  // Fase 7: endpoint único que devolve tudo do dashboard
  dashboard: async (usuario_id) => {
    const res = await api.get('/tarefas/dashboard', { params: { usuario_id } });
    return res.data;
  },
};

export default tarefaService;
